import {useQuery, useMutation, UseMutationOptions} from "@tanstack/react-query";
import {getImproveAPI} from "@/app2/shared/data/gen";
import {useTriggerPointsEarnedFloater} from "@/app2/shared/contexts/PointsEarnedFloaterContext";
import {resolveAwardedPointsValue} from "@/app2/shared/utils/resolveAwardedPointsValue";
import {useImproveAxios} from "@/app2/shared/hooks";
import type {
	GetInlineQuizQuestionsResponse,
	GetInlineQuizLatestResultResponse,
	InlineQuizQuestionDto,
	LessonEnrollmentsControllerGetInlineQuizQuestionsParams,
	SubmitInlineQuizRequest,
	SubmitInlineQuizResponse
} from "@/app2/shared/data/gen";
import {AxiosError} from "axios";

interface ErrorResponseData {
	statusCode: number;
	timestamp: string;
	path: string;
	method: string;
	message: string | string[];
	error?: string;
}

export type ApiErrorResponse = AxiosError<ErrorResponseData>;

export type {InlineQuizQuestionDto};

// Query keys for cache management
export const LESSON_ENROLLMENT_QUERY_KEYS = {
	all: ["lesson-enrollments"] as const,
	inlineQuiz: (enrollmentId: number, locale?: string) =>
		["lesson-enrollments", enrollmentId, "inline-quiz", locale] as const,
	questions: (enrollmentId: number, locale?: string) =>
		[...LESSON_ENROLLMENT_QUERY_KEYS.inlineQuiz(enrollmentId, locale), "questions"] as const,
	latestResult: (enrollmentId: number, locale?: string) =>
		[...LESSON_ENROLLMENT_QUERY_KEYS.inlineQuiz(enrollmentId, locale), "latest-result"] as const
};

/**
 * Get inline quiz questions for a lesson enrollment
 *
 * @param enrollmentId - The lesson enrollment ID
 * @param options - React Query options
 */
export const useGetInlineQuizQuestions = (
	enrollmentId: number | null | undefined,
	params?: LessonEnrollmentsControllerGetInlineQuizQuestionsParams,
	options?: {
		enabled?: boolean;
		staleTime?: number;
	}
) => {
	const axiosConfig = useImproveAxios();
	const {lessonEnrollmentsControllerGetInlineQuizQuestions} = getImproveAPI();

	return useQuery<GetInlineQuizQuestionsResponse, ApiErrorResponse>({
		queryKey: LESSON_ENROLLMENT_QUERY_KEYS.questions(enrollmentId!, params?.locale),
		queryFn: async () => {
			const response = await lessonEnrollmentsControllerGetInlineQuizQuestions(
				enrollmentId!,
				params,
				axiosConfig
			);
			return response.data;
		},
		enabled: !!enrollmentId && (options?.enabled !== false),
		staleTime: options?.staleTime ?? 5 * 60 * 1000 // 5 minutes
	});
};

/**
 * Get latest quiz result for a lesson enrollment
 *
 * @param enrollmentId - The lesson enrollment ID
 * @param options - React Query options
 */
export const useGetInlineQuizLatestResult = (
	enrollmentId: number | null | undefined,
	options?: {
		enabled?: boolean;
		staleTime?: number;
	}
) => {
	const axiosConfig = useImproveAxios();
	const {lessonEnrollmentsControllerGetInlineQuizLatestResult} = getImproveAPI();

	return useQuery<GetInlineQuizLatestResultResponse>({
		queryKey: LESSON_ENROLLMENT_QUERY_KEYS.latestResult(enrollmentId!),
		queryFn: async () => {
			const response = await lessonEnrollmentsControllerGetInlineQuizLatestResult(enrollmentId!, axiosConfig);
			return response.data;
		},
		enabled: !!enrollmentId && (options?.enabled !== false),
		staleTime: options?.staleTime ?? 1 * 60 * 1000 // 1 minute
	});
};

/**
 * Submit inline quiz answers for a lesson enrollment
 *
 * @param enrollmentId - The lesson enrollment ID
 * @param options - React Query mutation options
 */
export const useSubmitInlineQuiz = (
	enrollmentId: number | null | undefined,
	options?: UseMutationOptions<SubmitInlineQuizResponse, ApiErrorResponse, SubmitInlineQuizRequest>
) => {
	const axiosConfig = useImproveAxios();
	const {triggerPointsFloater} = useTriggerPointsEarnedFloater();
	const {lessonEnrollmentsControllerSubmitInlineQuiz} = getImproveAPI();
	const {onSuccess: optionsOnSuccess, ...restOptions} = options ?? {};

	return useMutation<SubmitInlineQuizResponse, ApiErrorResponse, SubmitInlineQuizRequest>({
		mutationFn: async (request: SubmitInlineQuizRequest) => {
			if (!enrollmentId) {
				throw new Error("Lesson enrollment ID is required");
			}
			const response = await lessonEnrollmentsControllerSubmitInlineQuiz(enrollmentId, request, axiosConfig);
			return response.data;
		},
		...restOptions,
		onSuccess: (data, variables, onMutateResult, context) => {
			const points = resolveAwardedPointsValue(data.awardedPoints);
			if (points != null) {
				triggerPointsFloater(points);
			}
			optionsOnSuccess?.(data, variables, onMutateResult, context);
		}
	});
};

